import React, { useEffect, useRef } from "react";

// Singleton loader for the YouTube IFrame API
let ytPromise;
function loadYT() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytPromise) return ytPromise;
  ytPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") prev();
      resolve(window.YT);
    };
    document.body.appendChild(tag);
  });
  return ytPromise;
}

/**
 * Viewport-aware, muted, looping YouTube player.
 * - Autoplays (muted) when >=50% visible; pauses when it leaves the viewport.
 * - Respects a manual user pause (won't force-replay after the user pauses).
 * - Keeps native YouTube controls so the user can unmute.
 * Design/layout is controlled by the parent via `className`.
 */
const AutoplayYouTube = ({ id, className = "", title = "" }) => {
  const wrapRef = useRef(null);
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const userPaused = useRef(false);
  const pausingFromObserver = useRef(false);

  useEffect(() => {
    let observer;
    let mounted = true;

    loadYT().then((YT) => {
      if (!mounted || !YT || !mountRef.current) return;
      playerRef.current = new YT.Player(mountRef.current, {
        videoId: id,
        width: "100%",
        height: "100%",
        playerVars: {
          autoplay: 0,
          mute: 1,
          loop: 1,
          playlist: id, // required for a single video to loop
          playsinline: 1,
          controls: 1,
          rel: 0,
          modestbranding: 1,
        },
        events: {
          onReady: (e) => {
            try {
              e.target.mute();
              const iframe = e.target.getIframe();
              if (iframe) {
                iframe.style.position = "absolute";
                iframe.style.inset = "0";
                iframe.style.width = "100%";
                iframe.style.height = "100%";
                if (title) iframe.title = title;
              }
            } catch (err) { /* noop */ }

            observer = new IntersectionObserver(
              ([entry]) => {
                const p = playerRef.current;
                if (!p || typeof p.playVideo !== "function") return;
                if (entry.isIntersecting) {
                  if (!userPaused.current) p.playVideo();
                } else {
                  pausingFromObserver.current = true;
                  p.pauseVideo();
                }
              },
              { threshold: 0.5 }
            );
            if (wrapRef.current) observer.observe(wrapRef.current);
          },
          onStateChange: (e) => {
            const S = window.YT && window.YT.PlayerState;
            if (!S) return;
            if (e.data === S.PAUSED) {
              // distinguish our (viewport) pause from a real user pause
              if (pausingFromObserver.current) pausingFromObserver.current = false;
              else userPaused.current = true;
            } else if (e.data === S.PLAYING) {
              userPaused.current = false;
            }
          },
        },
      });
    });

    return () => {
      mounted = false;
      if (observer) observer.disconnect();
      try {
        if (playerRef.current && playerRef.current.destroy) playerRef.current.destroy();
      } catch (err) { /* noop */ }
      playerRef.current = null;
    };
  }, [id, title]);

  return (
    <div ref={wrapRef} className={className}>
      <div ref={mountRef} className="w-full h-full" />
    </div>
  );
};

export default AutoplayYouTube;
